/**
 * Centralized feature flags enum for GrowthBook integration
 * Naming convention: [action][App][Subject] in pascalCase
 *
 * Usage: import { FeatureFlags } from '@/constants/featureFlags';
 */
export const FeatureFlags = {
  // API V2 Migration Flags - Resource Level
  USE_ADMIN_PANEL_API_V2_USERS: 'useAdminPanelApiV2Users',
  USE_ADMIN_PANEL_API_V2_ROLES: 'useAdminPanelApiV2Roles',
  USE_ADMIN_PANEL_API_V2_ORGANIZATIONS: 'useAdminPanelApiV2Organizations',
  USE_ADMIN_PANEL_API_V2_CODES: 'useAdminPanelApiV2Codes',
  USE_ADMIN_PANEL_API_V2_QUEUES: 'useAdminPanelApiV2Queues',
  USE_ADMIN_PANEL_API_V2_CALLS: 'useAdminPanelApiV2Calls',
  USE_ADMIN_PANEL_API_V2_VERSIONS: 'useAdminPanelApiV2Versions',
  USE_ADMIN_PANEL_API_V2_CMS1500_JOBS: 'useAdminPanelApiV2Cms1500Jobs',
  USE_ADMIN_PANEL_API_V2_DASHBOARD: 'useAdminPanelApiV2Dashboard',
  USE_ADMIN_PANEL_API_V2_LEAD_IMPORTS: 'useAdminPanelApiV2LeadImports',
  USE_ADMIN_PANEL_API_V2_LEADS: 'useAdminPanelApiV2Leads',
  USE_ADMIN_PANEL_API_V2_STATES: 'useAdminPanelApiV2States',
  USE_ADMIN_PANEL_API_V2_PERMISSION_TEMPLATES: 'useAdminPanelApiV2PermissionTemplates',
  USE_ADMIN_PANEL_API_V2_PERMISSIONS: 'useAdminPanelApiV2Permissions',
  USE_ADMIN_PANEL_API_V2_INSURANCE_PLANS: 'useAdminPanelApiV2InsurancePlans',
  USE_ADMIN_PANEL_API_V2_IMPERSONATION: 'useAdminPanelApiV2Impersonation',
  USE_ADMIN_PANEL_API_V2_PRODUCT_FEATURES: 'useAdminPanelApiV2ProductFeatures',
  USE_ADMIN_PANEL_API_V2_PARTNER_MARKETING_ASSETS: 'useAdminPanelApiV2PartnerMarketingAssets',

  // Feature Flags
  ENABLE_PATIENT_IMPERSONATION: 'enablePatientImpersonation',
  ENABLE_ADMIN_PANEL_RBAC: 'enableAdminPanelRBAC',
  ENABLE_ADMIN_ORGANIZATION_FEATURE_TOGGLES: 'enableAdminOrganizationFeatureToggles',
  ENABLE_ADMIN_PANEL_HEALTHIE_ENRICHMENT: 'enableAdminPanelHealthieEnrichment',
  ENABLE_ADMIN_PANEL_AUDIT_LOGGING: 'enableAdminPanelAuditLogging',

  // Global Migration Controls
  USE_ADMIN_PANEL_API_V2_GLOBAL_OVERRIDE: 'useAdminPanelApiV2GlobalOverride',
  ENABLE_ADMIN_PANEL_API_DEBUG_MODE: 'enableAdminPanelApiDebugMode',

  // Development and Testing
  ENABLE_ADMIN_PANEL_FEATURE_FLAG_DEBUG: 'enableAdminPanelFeatureFlagDebug',
  USE_ADMIN_PANEL_LOCAL_GROWTHBOOK: 'useAdminPanelLocalGrowthbook'
};

/**
 * Resource to flag mapping for programmatic access
 */
export const RESOURCE_TO_FLAG_MAP = {
  users: FeatureFlags.USE_ADMIN_PANEL_API_V2_USERS,
  roles: FeatureFlags.USE_ADMIN_PANEL_API_V2_ROLES,
  organizations: FeatureFlags.USE_ADMIN_PANEL_API_V2_ORGANIZATIONS,
  codes: FeatureFlags.USE_ADMIN_PANEL_API_V2_CODES,
  queues: FeatureFlags.USE_ADMIN_PANEL_API_V2_QUEUES,
  calls: FeatureFlags.USE_ADMIN_PANEL_API_V2_CALLS,
  versions: FeatureFlags.USE_ADMIN_PANEL_API_V2_VERSIONS,
  cms1500Jobs: FeatureFlags.USE_ADMIN_PANEL_API_V2_CMS1500_JOBS,
  dashboard: FeatureFlags.USE_ADMIN_PANEL_API_V2_DASHBOARD,
  leadImports: FeatureFlags.USE_ADMIN_PANEL_API_V2_LEAD_IMPORTS,
  leads: FeatureFlags.USE_ADMIN_PANEL_API_V2_LEADS,
  states: FeatureFlags.USE_ADMIN_PANEL_API_V2_STATES,
  permissionTemplates: FeatureFlags.USE_ADMIN_PANEL_API_V2_PERMISSION_TEMPLATES,
  permissions: FeatureFlags.USE_ADMIN_PANEL_API_V2_PERMISSIONS,
  insurancePlans: FeatureFlags.USE_ADMIN_PANEL_API_V2_INSURANCE_PLANS,
  impersonation: FeatureFlags.USE_ADMIN_PANEL_API_V2_IMPERSONATION,
  'product-features': FeatureFlags.USE_ADMIN_PANEL_API_V2_PRODUCT_FEATURES,
  'partner-marketing-assets': FeatureFlags.USE_ADMIN_PANEL_API_V2_PARTNER_MARKETING_ASSETS
};

/**
 * Get feature flag for a resource
 * @param {string} resource - Resource name (e.g., 'users', 'roles')
 * @returns {string} Feature flag name
 */
export const getFlagForResource = (resource) => {
  return RESOURCE_TO_FLAG_MAP[resource] || null;
};
